import React, { type JSX } from "react";

import YamlDocumentPreview_Component from "../../YamlDocumentPreview_Component";
import PdfPreviewPane from "./PdfPreviewPane";
import PreviewError from "./PreviewErrorState";
import { MarkdownView } from "./MarkdownPreview";
import {
  PreviewLoading,
  NoPreviewAvailable,
  UnknownDocumentType,
  DownloadOnlyPrompt,
  PlainTextView,
} from "./PreviewStates";
import { type DocumentData, filenameOf } from "./documentPreviewTypes";
import { type DocumentPreviewLoad } from "./useDocumentPreview";
import { type DocumentFacts } from "./useDocumentFacts";
import { type DocumentActions } from "./useDocumentActions";

/** Every document type whose preview is driven purely by the downloaded text. */
const TEXT_PREVIEWS: Record<string, (text: string) => JSX.Element> = {
  markdown: (text) => <MarkdownView text={text} />,
  text: (text) => <PlainTextView text={text} />,
  yaml: (text) => <YamlDocumentPreview_Component contentText={text} />,
};

export interface PreviewBodyProps {
  document: DocumentData;
  preview: DocumentPreviewLoad;
  facts: DocumentFacts;
  actions: DocumentActions;
}

function PreviewContent({ document, preview, facts, actions }: PreviewBodyProps): JSX.Element {
  if (preview.documentType === "pdf") {
    return (
      <PdfPreviewPane
        document={document}
        previewData={preview.previewData}
        objectUrl={preview.objectUrl}
        facts={facts}
        actions={actions}
      />
    );
  }

  const renderText = TEXT_PREVIEWS[preview.documentType];
  return renderText ? renderText(preview.contentText) : <UnknownDocumentType />;
}

/** Whatever the preview state calls for: progress, an error, or the document itself. */
export default function PreviewBody(props: PreviewBodyProps): JSX.Element {
  const { document, preview, actions } = props;

  if (preview.loading) {
    return <PreviewLoading progress={preview.downloadProgress} />;
  }

  if (preview.downloadOnly) {
    return (
      <DownloadOnlyPrompt
        filename={filenameOf(document)}
        downloading={actions.downloadingRaw}
        onDownload={() => void actions.downloadRaw()}
      />
    );
  }

  if (preview.error) {
    return (
      <PreviewError
        error={preview.error}
        objectUrl={preview.objectUrl}
        onRetry={() => void preview.reload()}
        onDownload={actions.download}
      />
    );
  }

  if (!preview.objectUrl) {
    return <NoPreviewAvailable />;
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden min-h-0">
      <PreviewContent {...props} />
    </div>
  );
}
